import React, { useCallback, useMemo, useState } from 'react';
import '../../../../../public/assets/jstree/themes/default/style.css';

const TREE_EXPANDED_STORAGE_KEY = 'webpages_browser_tree_expanded_v1';

function TreeNode({
  node,
  level,
  selectedKey,
  onSelect,
  isLast,
  onContextMenu,
  onDropItems,
  onDropDocumentInstances,
  onDragHover,
  isExpanded,
  onToggleExpanded,
}) {
  const nodeKey = String(node.nodeKey || node.id);
  const isSelected = String(selectedKey) === nodeKey;
  const isBranchActive = !isSelected && !!selectedKey && String(selectedKey).startsWith(`${nodeKey}:`);
  const [isDragOver, setIsDragOver] = useState(false);
  const hasChildren = Array.isArray(node.children) && node.children.length > 0;
  const isAll = !!node.isAll || String(node.id) === 'all';
  const isVirtual = !!node.isAll || !!node.isTypeRoot || !!node.isRoot;

  const handleToggle = (e) => {
    e.stopPropagation();
    if (hasChildren) {
      if (typeof onToggleExpanded === 'function') {
        onToggleExpanded(nodeKey, !isExpanded);
      }
    }
  };

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect(node);
  };

  const handleDragEnter = (e) => {
    if (!onDropItems || isVirtual) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
    if (onDragHover) onDragHover(node, true);
  };

  const handleDragLeave = (e) => {
    if (!onDropItems || isVirtual) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (onDragHover) onDragHover(node, false);
  };

  const handleDragOver = (e) => {
    if (!onDropItems || isVirtual) return;
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    if (!onDropItems || isVirtual) return;
    setIsDragOver(false);
    if (onDragHover) onDragHover(node, false);
    const rawItems = e.dataTransfer.getData('item-ids');
    const rawInstances = e.dataTransfer.getData('document-instance-ids');
    if (!rawItems && !rawInstances) return;
    e.preventDefault();
    e.stopPropagation();

    if (rawInstances && typeof onDropDocumentInstances === 'function') {
      const ids = rawInstances
        .split(',')
        .map((v) => parseInt(v, 10))
        .filter(Boolean);
      if (!ids.length) return;
      onDropDocumentInstances(node.id, ids);
      return;
    }

    const raw = rawItems;
    if (!raw) return;
    const ids = raw
      .split(',')
      .map((v) => parseInt(v, 10))
      .filter(Boolean);
    if (!ids.length) return;
    onDropItems(node.id, ids);
  };

  const nodeClasses = [
    'jstree-node',
    hasChildren && isExpanded ? 'jstree-open' : '',
    hasChildren && !isExpanded ? 'jstree-closed' : '',
    !hasChildren ? 'jstree-leaf' : '',
    isLast ? 'jstree-last' : ''
  ].filter(Boolean).join(' ');

  const anchorClasses = [
    'jstree-anchor',
    isSelected ? 'jstree-clicked' : ''
  ].filter(Boolean).join(' ');

  return (
    <li
      className={nodeClasses}
      role="treeitem"
      aria-selected={isSelected}
      aria-expanded={isExpanded}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <i className="jstree-icon jstree-ocl" role="presentation" onClick={handleToggle}></i>

      <a
        className={anchorClasses}
        href="#"
        onClick={handleClick}
        onContextMenu={(e) => {
          if (!onContextMenu || isVirtual) return;
          e.preventDefault();
          e.stopPropagation();
          onContextMenu(e, node);
        }}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{
          transition: 'transform 0.2s ease, background-color 0.2s ease',
          transform: isDragOver ? 'scale(1.05)' : 'none',
          backgroundColor: isDragOver ? '#e6f7ff' : 'transparent',
          borderRadius: '4px',
          display: 'inline-block',
          paddingRight: '5px',
          fontWeight: isSelected || isBranchActive ? 600 : undefined,
        }}
      >
        {node.isAll ? (
          <i className="jstree-icon jstree-themeicon fa fa-list jstree-themeicon-custom" role="presentation"></i>
        ) : node.isRoot ? (
          <i className={`jstree-icon jstree-themeicon ${node.icon || 'fa fa-folder'} jstree-themeicon-custom`} role="presentation"></i>
        ) : node.isTypeRoot ? (
          <i className={`jstree-icon jstree-themeicon ${node.icon || 'fa fa-folder'} jstree-themeicon-custom`} role="presentation"></i>
        ) : (
          <span style={{ position: 'relative', display: 'inline-block' }}>
            <i className="jstree-icon jstree-themeicon fa fa-folder jstree-themeicon-custom" role="presentation"></i>
            {node.has_pages ? (
              <i
                className="fa fa-file-text-o"
                aria-hidden="true"
                title="Contains pages"
                style={{
                  position: 'absolute',
                  top: -4,
                  right: -4,
                  fontSize: 9,
                  color: '#337ab7'
                }}
              />
            ) : null}
          </span>
        )}
        <span>{node.title}</span>
      </a>

      {hasChildren && isExpanded ? (
        <ul role="group" className="jstree-children">
          {node.children.map((child, index) => (
            <TreeNode
              key={String(child.nodeKey || child.id)}
              node={child}
              level={level + 1}
              selectedKey={selectedKey}
              onSelect={onSelect}
              onContextMenu={onContextMenu}
              onDropItems={onDropItems}
              onDropDocumentInstances={onDropDocumentInstances}
              onDragHover={onDragHover}
              isExpanded={typeof onToggleExpanded === 'function' ? onToggleExpanded.getExpanded(String(child.nodeKey || child.id)) : true}
              onToggleExpanded={onToggleExpanded}
              isLast={index === node.children.length - 1}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function cloneFoldersWithNodeKeys(folders, parentKey) {
  return (folders || []).map((f) => {
    const nodeKey = `${parentKey}:folder:${f.id}`;
    return {
      ...f,
      nodeKey,
      children: cloneFoldersWithNodeKeys(f.children || [], nodeKey)
    };
  });
}

export default function WebpagesFolderTree({ folders, selectedKey, onSelect, onContextMenu, onDropItems, onDropDocumentInstances, onDragHover }) {
  const [expandedByKey, setExpandedByKey] = useState(() => {
    try {
      const raw = window.localStorage.getItem(TREE_EXPANDED_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (!parsed || typeof parsed !== 'object') return {};
      return parsed;
    } catch {
      return {};
    }
  });

  const getExpanded = useCallback(
    (nodeKey) => {
      const key = String(nodeKey);
      const v = expandedByKey[key];
      if (typeof v === 'boolean') return v;
      return true;
    },
    [expandedByKey]
  );

  const setExpanded = useCallback((nodeKey, nextExpanded) => {
    const key = String(nodeKey);
    const value = !!nextExpanded;
    setExpandedByKey((prev) => {
      const next = { ...(prev || {}), [key]: value };
      try {
        window.localStorage.setItem(TREE_EXPANDED_STORAGE_KEY, JSON.stringify(next));
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  const nodes = useMemo(() => {
    const folderNodes = cloneFoldersWithNodeKeys(folders || [], 'root');

    const pageSubGroups = [
      { id: 'type_pages_templates', title: 'Templates', icon: 'fa fa-file-text-o', filterTypeIds: [13], filterKinds: ['template'] },
      { id: 'type_pages_components', title: 'Components', icon: 'fa fa-file-text-o', filterTypeIds: [13], filterKinds: ['component'] },
      { id: 'type_pages_pages', title: 'Pages', icon: 'fa fa-file-text-o', filterTypeIds: [13], filterKinds: ['page'] },
    ].map((t) => ({
      id: t.id,
      nodeKey: t.id,
      title: t.title,
      icon: t.icon,
      isTypeRoot: true,
      filterTypeIds: t.filterTypeIds,
      filterKinds: t.filterKinds,
      children: [],
    }));

    const typeRoots = [
      { id: 'type_pages', title: 'Pages', icon: 'fa fa-file-text-o', filterTypeIds: [13], filterKinds: ['page', 'template', 'component'], children: pageSubGroups },
      { id: 'type_documents', title: 'Media', icon: 'fa fa-file', filterTypeIds: [9], filterKinds: ['document'] },
      { id: 'type_reports', title: 'Reports', icon: 'fa fa-bar-chart', filterTypeIds: [7], filterKinds: ['report'] },
      { id: 'type_audiences', title: 'Audiences', icon: 'fa fa-users', filterTypeIds: [7], filterKinds: ['audience'] },
      { id: 'type_emails', title: 'Emails', icon: 'fa fa-envelope', filterTypeIds: [17], filterKinds: ['email'] },
      { id: 'type_lists', title: 'Lists', icon: 'fa fa-list-alt', filterTypeIds: [8], filterKinds: ['list'] },
      { id: 'type_forms', title: 'Forms', icon: 'fa fa-wpforms', filterTypeIds: [4], filterKinds: ['form'] },
      { id: 'type_composite_reports', title: 'Composite Reports', icon: 'fa fa-clone', filterTypeIds: [32], filterKinds: ['composite_report'] },
      { id: 'type_campaigns', title: 'Campaigns', icon: 'fa fa-bullhorn', filterTypeIds: [38], filterKinds: ['campaign'] },
    ].map((t) => ({
      id: t.id,
      nodeKey: t.id,
      title: t.title,
      icon: t.icon,
      isTypeRoot: true,
      filterTypeIds: t.filterTypeIds,
      filterKinds: t.filterKinds,
      children: Array.isArray(t.children) ? t.children : [],
    }));

    const allNode = {
      id: 'all',
      nodeKey: 'all',
      title: 'All',
      isAll: true,
      children: typeRoots,
    };

    const rootNode = {
      id: 'root',
      nodeKey: 'root',
      title: 'Root',
      isRoot: true,
      icon: 'fa fa-folder-open',
      children: [allNode, ...folderNodes],
    };

    return [rootNode];
  }, [folders]);

  const handleSelect = useCallback(
    (node) => {
      onSelect(node);
    },
    [onSelect]
  );

  return (
    <div className="jstree jstree-default wb-webpages-tree" role="tree">
      <style>
        {`
          /* Remove the wasted left gutter + root vertical guide line */
          .wb-webpages-tree > .jstree-container-ul {
            background: none !important;
            padding-left: 0 !important;
          }

          /* Keep indentation/connector styling for nested levels intact, but don't draw root-level guide column */
          .wb-webpages-tree > .jstree-container-ul > .jstree-node {
            background: none !important;
            margin-left: 0 !important;
          }

          /* Match the desired "flush" root look: remove the left toggle gutter on the root node */
          .wb-webpages-tree > .jstree-container-ul > .jstree-node > .jstree-ocl {
            width: 0 !important;
            min-width: 0 !important;
          }

          /* First-level children (directly under Root) should be flush (margin-left: 0) */
          .wb-webpages-tree > .jstree-container-ul > .jstree-node > .jstree-children > .jstree-node {
            margin-left: 0 !important;
          }
        `}
      </style>
      <ul className="jstree-container-ul jstree-children" role="group">
        {nodes.map((node, index) => (
          <TreeNode
            key={String(node.nodeKey || node.id)}
            node={node}
            level={0}
            selectedKey={selectedKey}
            onSelect={handleSelect}
            onContextMenu={onContextMenu}
            onDropItems={onDropItems}
            onDropDocumentInstances={onDropDocumentInstances}
            onDragHover={onDragHover}
            isExpanded={getExpanded(String(node.nodeKey || node.id))}
            onToggleExpanded={Object.assign(setExpanded, { getExpanded })}
            isLast={index === nodes.length - 1}
          />
        ))}
      </ul>
    </div>
  );
}
